import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Text,
  ResponsiveContainer,
  CartesianGrid
} from 'recharts';
import { AssetAllocationTypeChartData } from 'store/modules/Charts/types';
import { dummyData } from '../Utils';
import { formatPercent } from 'utils';
import MorningstarCopyright from 'components/MorningstarCopyright/MorningstarCopyright';
import { existingVsRecommendedIndex } from 'components/Settings/Palette/utils';
import { useTheme } from '@material-ui/styles';
import { radiusHorizontal } from 'utils/charts';
import { tooltipStylePadded } from '../Tooltips/utils';

interface Keyable {
  [key: string]: any;
}

interface AssetAllocationBarProps {
  data: Array<AssetAllocationTypeChartData>;
  isAnimationActive?: boolean;
  hasExistingPolicies?: boolean;
  bigLabels?: boolean;
  existingOnly?: boolean;
}
const Label = (val: any) => {
  if (val.value === 0) return null;
  const offset = (val.height + val.offset) / 2;
  return (
    <text height={val.height} x={val.x + val.width + 10} y={val.y + offset + 1}>
      <tspan fill={val.fill}>{formatPercent(val.value)}</tspan>
    </text>
  );
};
const Tick = ({ payload: { value }, bigLabels, ...rest }: any) => (
  <text
    {...rest}
    className="bar-chart-tick"
    dy={12}
    fontSize={bigLabels ? '16px' : 'inherit'}
  >
    {value}%
  </text>
);

const getTextWidth = (text: string, font: any) => {
  /* eslint-disabled */
  // @ts-ignore
  const canvas = document.createElement('canvas');
  const context: any = canvas.getContext('2d');
  context.font = font;
  const metrics = context.measureText(text);
  return metrics.width;
};

const CustomizedAxisTick = (props: any) => {
  const { payload, bigLabels } = props;

  return (
    <Text
      {...props}
      style={{
        fontSize: bigLabels ? '16px' : 'inherit'
      }}
    >
      {payload.value}
    </Text>
  );
};

const AssetAllocationBar = ({
  data,
  hasExistingPolicies = true,
  bigLabels = false,
  existingOnly = false
}: AssetAllocationBarProps) => {
  const theme = useTheme() as any;
  const isDataProvided = data && data.length > 0;
  const dataToRender = isDataProvided ? data : dummyData.assetAllocations;

  const filteredData = isDataProvided
    ? dataToRender.filter(
        (data: any) => data.Existing !== 0 || data.Recommended !== 0
      )
    : dataToRender;

  // @ts-ignore
  const adjustToPercentage = filteredData.map((data: any) => {
    const modifiedData = {
      ...data,
      Existing: data.Existing * 100,
      Recommended: data.Recommended ? data.Recommended * 100 : 0
    };

    return modifiedData;
  });

  const labels =
    [...adjustToPercentage].length > 0
      ? [...adjustToPercentage].sort((a, b) => b.name.length - a.name.length)[0]
      : { name: 'Alternative investments' };

  const maxWidthLabel = getTextWidth(labels.name, 16);
  return (
    <div>
      <ResponsiveContainer width="100%" height="100%" minHeight={820}>
        <BarChart
          data={adjustToPercentage}
          margin={{ top: 5, right: 40, left: 0, bottom: 5 }}
          layout="vertical"
          barGap={5}
          barCategoryGap={10}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            horizontalCoordinatesGenerator={props => {
              const data = [] as Array<any>;
              if (props.yAxis) {
                const yAxis = props.yAxis as Keyable;
                yAxis.domain.forEach((domain: string, index: number) => {
                  data.push(
                    yAxis.bandSize * index + (props.offset as Keyable).top
                  );
                });
              }
              return data;
            }}
          />
          <XAxis type="number" tick={<Tick bigLabels={bigLabels} />} />
          <YAxis
            dataKey="name"
            type="category"
            width={maxWidthLabel + 25}
            tick={<CustomizedAxisTick bigLabels={bigLabels} />}
            tickLine={false}
          />
          <Tooltip
            formatter={(val: any) => formatPercent(val)}
            contentStyle={tooltipStylePadded}
          />
          <Legend wrapperStyle={{ fontSize: bigLabels ? '16px' : 'inherit' }} />
          {!existingOnly && (
            <Bar
              dataKey="Recommended"
              fill={
                theme.palette.baseColors.charts['comparisons'][
                  existingVsRecommendedIndex('recommended')
                ]
              }
              label={
                <Label
                  fill={
                    theme.palette.baseColors.charts['comparisons'][
                      existingVsRecommendedIndex('recommended')
                    ]
                  }
                />
              }
              isAnimationActive={false}
              radius={radiusHorizontal}
            ></Bar>
          )}
          {hasExistingPolicies && (
            <Bar
              dataKey="Existing"
              fill={
                theme.palette.baseColors.charts['comparisons'][
                  existingVsRecommendedIndex('existing')
                ]
              }
              label={
                <Label
                  fill={
                    theme.palette.baseColors.charts['comparisons'][
                      existingVsRecommendedIndex('existing')
                    ]
                  }
                />
              }
              isAnimationActive={false}
              radius={radiusHorizontal}
            ></Bar>
          )}
        </BarChart>
      </ResponsiveContainer>
      <MorningstarCopyright />
    </div>
  );
};

export default AssetAllocationBar;
